// FLO v3.0 weight engine — "operational bridge" from the current KYRS task
// schema to the manual's v3.0 river/state model (see
// scratchpad/FLO_RIVER_SPEC.md, section "Weight engine", for the contract
// this file implements). Pure TypeScript, no React, no Supabase — every
// export here is a plain function over plain data so it stays
// unit-testable and reusable by both `FloRiver.tsx` and `FloStatsDeck.tsx`.
//
// What "v3.0 operational bridge" means: the manual's v3.0 river model wants
// a *state* per open task (paused / overdue / scheduled / unprocessed) and a
// *weight* that blends the existing FLO priority score with that state via a
// fixed multiplier table. The current schema has no explicit state column —
// state is inferred from `done` / `unlisted` / `tabled` / `scheduled`
// (see `inferState` below), mirroring how `computeLoad` in `flo.ts` already
// treats `done`/`unlisted` as exclusion signals.
//
// Normalization approximation: v3.0 wants priority on a bounded 0-10 scale,
// but `calcTaskPriorityIntended` (flo.ts) is an unbounded sum of group
// numbers + value-criteria scores + a manual add, so there is no fixed
// theoretical max to divide by. This file approximates "10 = heaviest thing
// you actually have open right now" by normalizing every open task's raw
// priority against the max raw priority among all *currently open, included*
// tasks (`maxOpenRawPriority`, floored at 1 to avoid divide-by-zero on an
// all-zero-priority backlog). This is a per-call, per-user relative scale,
// not a global absolute one — documented here per the spec's requirement to
// call out the approximation inline.
//
// Keep in sync with the spec's field names — `FloRiver.tsx` /
// `FloStatsDeck.tsx` read `RiverItem`/`RiverModel` fields directly.

import type { FloGroup, FloTaskLike, FloValueSetting } from './flo';
import { calcTaskPriorityIntended } from './flo';
import type { Sector } from '../domain/olmps';

// ============================================================================
// Input shapes
// ============================================================================

/**
 * `loadFloInputs` (flo.ts) returns `FloInputs.tasks: FloTaskLike[]`, which
 * has no `tabled` field (it isn't needed by Load/Charge/Flow). The v3.0
 * 'paused' state does need it, so the integrator (NumaTool.tsx) should merge
 * the raw `tasks.tabled` column onto each `FloTaskLike` before calling
 * `buildRiverModel` — this type documents that expectation.
 */
export type FloRiverTask = FloTaskLike & { tabled?: boolean; /** tasks.notes (free text; NUMA reps are appended as dated lines). */ notes?: string | null };

/** Prefix of the dated line NUMA appends to tasks.notes for a stone's rep:
 *  `[NUMA 2026-10-07] reschedule Friday, delegating the call`. */
export const NUMA_NOTE_PREFIX = '[NUMA ';

export function formatNumaNote(date: Date, text: string): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${NUMA_NOTE_PREFIX}${date.getFullYear()}-${p(date.getMonth() + 1)}-${p(date.getDate())}] ${text.trim()}`;
}

/** The text of the most recent NUMA line in a task's notes, or null. */
export function lastNumaNote(notes: string | null | undefined): string | null {
  if (!notes) return null;
  const lines = notes.split('\n').filter((l) => l.startsWith(NUMA_NOTE_PREFIX));
  const last = lines[lines.length - 1];
  if (!last) return null;
  const close = last.indexOf('] ');
  return close >= 0 ? last.slice(close + 2).trim() || null : null;
}

/** Minimal `task_lists` shape needed for sector/list-name lookups. Not part
 *  of `FloInputs` — the integrator fetches `task_lists` separately (see
 *  spec, Component 3). */
export interface FloRiverList {
  id: string;
  name: string;
  sector: Sector | null;
}

// ============================================================================
// State inference
// ============================================================================

export type FloV3State = 'excluded' | 'paused' | 'overdue' | 'scheduled' | 'unprocessed';

const STATE_MODIFIERS: Record<FloV3State, number> = {
  excluded: 0,
  paused: 0.1,
  overdue: 1.25,
  scheduled: 0.35,
  unprocessed: 1,
};

/** State → weight multiplier (spec "Weight engine" table). `excluded` is not
 *  a real v3.0 state — it's this file's marker for "don't render" (done or
 *  unlisted tasks), returned so callers can filter without a second check. */
export function stateModifier(state: FloV3State): number {
  return STATE_MODIFIERS[state];
}

function startOfLocalDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
}

/**
 * Parses a KYRS `scheduled` string into a `Date`. Mirrors
 * `KyrsTool.tsx#parseScheduledMillis` (lines ~314-331) defensively:
 * `yyyy-MM-dd HH:mm` / `yyyy-MM-dd[T]HH:mm`, `yyyy-MM-dd`, and the
 * case-insensitive keywords `today`/`tomorrow`/`yesterday` (resolved
 * relative to `now`, local midnight). Returns `null` for anything else
 * (including empty/missing strings) — same "unparseable → treat as
 * unscheduled" contract as the rest of FLO.
 */
export function parseScheduledDate(scheduled: string | null | undefined, now: Date = new Date()): Date | null {
  if (!scheduled) return null;
  const s = scheduled.trim();
  if (!s) return null;

  let m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(s);
  if (m) {
    return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]));
  }
  m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (m) {
    return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  }

  const lower = s.toLowerCase();
  const today = startOfLocalDay(now);
  if (lower === 'today') return today;
  if (lower === 'tomorrow') return new Date(today.getTime() + 86400000);
  if (lower === 'yesterday') return new Date(today.getTime() - 86400000);
  return null;
}

/** Whole local days between two dates (`b` minus `a`), truncating time of
 *  day on both sides first so a same-calendar-day pair is always 0. */
function daysBetweenLocalDates(a: Date, b: Date): number {
  const msPerDay = 86400000;
  return Math.round((startOfLocalDay(b).getTime() - startOfLocalDay(a).getTime()) / msPerDay);
}

/**
 * Infers the v3.0 state of a task from the current schema:
 *   done === true       → 'excluded' (weight 0, never rendered)
 *   unlisted === true   → 'excluded' (consistent with computeLoad's exclusion)
 *   tabled === true     → 'paused'
 *   scheduled < today   → 'overdue'
 *   scheduled >= today  → 'scheduled'
 *   scheduled unparseable/null → 'unprocessed'
 */
export function inferState(task: FloRiverTask, now: Date = new Date()): FloV3State {
  if (task.done) return 'excluded';
  if (task.unlisted) return 'excluded';
  if (task.tabled) return 'paused';

  const scheduledDate = parseScheduledDate(task.scheduled, now);
  if (scheduledDate === null) return 'unprocessed';

  const today = startOfLocalDay(now);
  return scheduledDate.getTime() < today.getTime() ? 'overdue' : 'scheduled';
}

// ============================================================================
// River model
// ============================================================================

export type RiverZone = 'intake' | 'window' | 'later' | 'floor';

export interface RiverItem {
  id: string;
  title: string;
  listId: string | null;
  listName: string;
  sector: Sector | null;
  state: FloV3State;
  stateModifier: number;
  rawPriority: number;
  normalizedPriority: number;
  weight: number;
  scheduledAt: Date | null;
  scheduledRaw: string | null;
  /** 0..6 within the 7-day window (today = 0), only set when `zone === 'window'`. */
  dayIndex: number | null;
  zone: RiverZone;
  daysOverdue: number;
  sinkProgress: number;
  isStone: boolean;
  /** Last NUMA "what are you doing about it?" line saved on this task, if any. */
  lastNote: string | null;
}

export type FloLoadBand = 'Light' | 'Normal' | 'Heavy' | 'Overloaded';

export interface RiverModel {
  items: RiverItem[];
  /** Sum of `weight` across all included (rendered) items. */
  totalLoad: number;
  loadBand: FloLoadBand;
  /** Baseline the load band / green band were judged against. */
  baseline: number;
  /** Open weight per sector key ('S'|'H'|'A'|'P'|'E'|'—' for no-sector). */
  bySector: Record<string, number>;
  /** Top 6 items by weight, descending. */
  offenders: RiverItem[];
}

/**
 * FALLBACK Load baseline on the river's normalised-weight scale. A rolling
 * personal baseline needs persisted daily-load history, which the schema does
 * not store (no flow-history table) and the brief forbids inventing one, so
 * every caller uses this constant until such history exists. Pass a personal
 * value as `baseline` to buildRiverModel / greenStatus when one is available.
 */
export const FALLBACK_BASELINE_LOAD = 22;
/** @deprecated alias of FALLBACK_BASELINE_LOAD (kept for existing imports). */
export const BASELINE_LOAD = FALLBACK_BASELINE_LOAD;

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

function computeZone(
  state: FloV3State,
  isStone: boolean,
  scheduledDate: Date | null,
  now: Date
): { zone: RiverZone; dayIndex: number | null } {
  if (state === 'paused' || state === 'unprocessed' || scheduledDate === null) {
    return { zone: 'intake', dayIndex: null };
  }
  if (isStone) {
    // Stones rest on the floor under their due-day column; since a stone is
    // by definition >=3 days overdue (before the window start), there is no
    // real "due-day column" inside the 7-day window — pin to the leftmost
    // (today) column, per spec's "or leftmost if due before window".
    return { zone: 'floor', dayIndex: 0 };
  }

  const diffDays = daysBetweenLocalDates(startOfLocalDay(now), scheduledDate);
  if (diffDays >= 0 && diffDays <= 6) {
    return { zone: 'window', dayIndex: diffDays };
  }
  if (diffDays > 6) {
    return { zone: 'later', dayIndex: null };
  }
  // diffDays < 0: overdue (daysOverdue 1-2) but not yet a stone. The spec's
  // zone list has no dedicated bucket for "before window, not yet a stone"
  // — treated the same as stones: window zone, leftmost/today column, so it
  // visually sinks in place ahead of becoming a floor stone.
  return { zone: 'window', dayIndex: 0 };
}

function loadBandFor(totalLoad: number, baseline: number): FloLoadBand {
  const ratio = totalLoad / baseline;
  if (ratio < 0.75) return 'Light';
  if (ratio <= 1.25) return 'Normal';
  if (ratio <= 1.75) return 'Heavy';
  return 'Overloaded';
}

/**
 * Builds the full river model from raw FLO inputs. Excludes done/unlisted
 * tasks entirely (per `inferState`'s 'excluded' state) — they are never
 * present in `items`.
 */
export function buildRiverModel(
  tasks: FloRiverTask[],
  lists: FloRiverList[],
  groups: FloGroup[],
  valueSettings: FloValueSetting[],
  unscheduledBonus: number,
  now: Date = new Date(),
  baseline: number = FALLBACK_BASELINE_LOAD
): RiverModel {
  const listById = new Map(lists.map((l) => [l.id, l]));

  const included = tasks.filter((t) => inferState(t, now) !== 'excluded');
  const rawPriorities = included.map((t) => calcTaskPriorityIntended(t, groups, valueSettings, unscheduledBonus));
  // Guarded at a min of 1 so an all-zero-priority backlog doesn't divide by 0.
  const maxOpenRawPriority = Math.max(1, ...rawPriorities, 0);

  const items: RiverItem[] = included.map((task, i) => {
    const state = inferState(task, now);
    const modifier = stateModifier(state);
    const rawPriority = rawPriorities[i];
    const normalizedPriority = round2((10 * rawPriority) / maxOpenRawPriority);
    const weight = round2(normalizedPriority * modifier);

    const scheduledAt = parseScheduledDate(task.scheduled, now);
    const daysOverdue = state === 'overdue' && scheduledAt ? Math.max(0, daysBetweenLocalDates(scheduledAt, now)) : 0;
    const sinkProgress = round2(Math.min(1, daysOverdue / 3));
    const isStone = sinkProgress >= 1;

    const { zone, dayIndex } = computeZone(state, isStone, scheduledAt, now);
    const list = task.listId ? listById.get(task.listId) : undefined;

    const item: RiverItem = {
      id: task.id,
      title: task.title,
      listId: task.listId,
      listName: list?.name ?? 'Unlisted',
      sector: list?.sector ?? null,
      state,
      stateModifier: modifier,
      rawPriority,
      normalizedPriority,
      weight,
      scheduledAt,
      scheduledRaw: task.scheduled,
      dayIndex,
      zone,
      daysOverdue,
      sinkProgress,
      isStone,
      lastNote: lastNumaNote(task.notes),
    };
    return item;
  });

  const totalLoad = round2(items.reduce((sum, it) => sum + it.weight, 0));
  const loadBand = loadBandFor(totalLoad, baseline);

  const bySector: Record<string, number> = {};
  for (const it of items) {
    const key = it.sector ?? '—';
    bySector[key] = round2((bySector[key] ?? 0) + it.weight);
  }

  const offenders = items
    .slice()
    .sort((a, b) => b.weight - a.weight)
    .slice(0, 6);

  return { items, totalLoad, loadBand, baseline, bySector, offenders };
}
